<template>
  <div class="d-flex align-center">
    <v-select
      v-model="selectedCompany"
      :items="companies"
      item-title="name"
      item-value="id"
      label="Company"
      variant="outlined"
      density="compact"
      hide-details
      class="company-switcher"
      :style="responsiveStyle"
      @update:modelValue="switchCompany"
    >
      <template v-slot:prepend-inner>
        <v-icon size="small">mdi-domain</v-icon>
      </template>
      
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props">
          <template v-slot:prepend>
            <v-avatar size="24" color="primary">
              <span class="text-caption">{{ item.raw.code?.charAt(0)?.toUpperCase() || 'C' }}</span>
            </v-avatar>
          </template>
          <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ item.raw.code }}</v-list-item-subtitle>
        </v-list-item>
      </template>

      <template v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <v-avatar size="20" color="primary" class="mr-2">
            <span class="text-caption">{{ item.raw.code?.charAt(0)?.toUpperCase() || 'C' }}</span>
          </v-avatar>
          <span class="text-truncate">{{ item.raw.name }}</span>
        </div>
      </template>
    </v-select>

    <!-- Connection Status -->
    <v-tooltip v-if="currentCompany" bottom>
      <template v-slot:activator="{ props }">
        <v-btn
          v-bind="props"
          :icon="connectionStatus.icon"
          :color="connectionStatus.color"
          size="x-small"
          variant="text"
          density="compact"
          class="ml-1 flex-shrink-0"
          @click="testConnection"
          :loading="testingConnection"
          style="min-width: 24px; width: 24px; height: 24px;"
        />
      </template>
      <span>{{ connectionStatus.tooltip }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'CompanySwitcher',
  data() {
    return {
      testingConnection: false,
      lastConnectionTest: null
    }
  },
  computed: {
    ...mapState('companies', ['companies', 'currentCompany']),
    ...mapGetters('companies', ['hasCompanies']),
    selectedCompany: {
      get() {
        return this.currentCompany?.id || null
      },
      set(value) {
        // Handled by switchCompany method
      }
    },
    connectionStatus() {
      if (!this.lastConnectionTest) {
        return {
          icon: 'mdi-help-circle',
          color: 'grey',
          tooltip: 'Click to test connection'
        }
      }
      
      if (this.lastConnectionTest.success) {
        return {
          icon: 'mdi-check-circle',
          color: 'success',
          tooltip: 'Connection successful'
        }
      } else {
        return {
          icon: 'mdi-alert-circle',
          color: 'error',
          tooltip: `Connection failed: ${this.lastConnectionTest.error}`
        }
      }
    },
    responsiveStyle() {
      if (this.$vuetify.display.xs) {
        return 'min-width: 120px; max-width: 160px; flex: 1;'
      } else if (this.$vuetify.display.sm) {
        return 'min-width: 140px; max-width: 180px; flex: 1;'
      } else {
        return 'min-width: 160px; max-width: 220px; flex: 1;'
      }
    }
  },
  methods: {
    ...mapActions('companies', ['fetchMyCompanies', 'setCurrentCompany', 'testConnection']),
    ...mapActions('dashboard', ['clearData', 'fetchDashboardData']),
    ...mapActions(['showSnackbar']),
    
    async switchCompany(companyId) {
      if (!companyId) return
      
      const company = this.companies.find(c => c.id === companyId)
      if (company) {
        this.setCurrentCompany(company)
        this.clearData()
        this.lastConnectionTest = null
        
        // Automatically test connection
        await this.testConnectionForCompany(companyId)
        
        // Refresh dashboard data if on dashboard page
        if (this.$route.name === 'Dashboard') {
          this.fetchDashboardData(companyId)
        }
        
        this.showSnackbar({
          message: `Switched to ${company.name}`,
          color: 'success'
        })
      }
    },
    
    async testConnectionForCompany(companyId) {
      this.testingConnection = true
      try {
        const result = await this.testConnection(companyId)
        this.lastConnectionTest = result
        
        if (result.success) {
          this.showSnackbar({
            message: 'Connection successful',
            color: 'success'
          })
        } else {
          this.showSnackbar({
            message: `Connection failed: ${result.error}`,
            color: 'error'
          })
        }
      } catch (error) {
        this.lastConnectionTest = {
          success: false,
          error: error.message
        }
      } finally {
        this.testingConnection = false
      }
    }
  },
  async created() {
    // Fetch user's companies
    await this.fetchMyCompanies()
    
    // If no current company is selected but companies are available, select the first one
    if (!this.currentCompany && this.companies.length > 0) {
      this.switchCompany(this.companies[0].id)
    }
  }
}
</script>

<style scoped>
.company-switcher {
  flex: 1;
}

.company-switcher :deep(.v-field) {
  font-size: 0.875rem;
}

.company-switcher :deep(.v-field__input) {
  padding-top: 8px;
  padding-bottom: 8px;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive text truncation */
@media (max-width: 600px) {
  .text-truncate {
    max-width: 100px;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .text-truncate {
    max-width: 120px;
  }
}

@media (min-width: 961px) {
  .text-truncate {
    max-width: 160px;
  }
}
</style>